import { useEffect, useRef, useState } from 'react';
import { LogOut, ShieldCheck, UserRound } from 'lucide-react';
import { useAuth } from '../lib/auth/index.ts';
import { useMe } from '../lib/api.ts';

/**
 * Who am I, and how do I leave.
 *
 * Built from primitives rather than a menu library: this needs exactly one
 * dropdown, and the accessibility contract for it — labelled trigger, Escape to
 * close, focus returned to the trigger — is smaller than the dependency.
 */
export function UserMenu() {
  const auth = useAuth();
  const me = useMe(auth.status === 'authenticated');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Focus must come back to the trigger, or a keyboard user is dropped at
      // the top of the document with no idea where they were.
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const profile = me.data?.data;
  // Falls back to the id token's claims so the menu shows a name during the
  // first render, before /v1/me answers — but the API's answer wins once it
  // arrives, because it is the one that reflects a changed display name.
  const email = profile?.user.email ?? auth.email ?? '';
  const name = profile?.user.display_name ?? auth.displayName ?? email;
  const platformRoles = profile?.platform_roles ?? [];

  if (auth.status !== 'authenticated') return null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        // The email is the label, not just decoration: "Account menu" alone
        // leaves a screen-reader user unable to tell which account they are in.
        aria-label={`Account menu for ${email}`}
        onClick={() => setOpen((value) => !value)}
        className={
          'flex h-7 items-center gap-1.5 rounded-[var(--vc-radius-md)] px-2 ' +
          'text-[12.5px] text-[var(--vc-text-muted)] transition-colors ' +
          'duration-[var(--vc-duration-fast)] hover:bg-[var(--vc-surface-hover)] ' +
          'hover:text-[var(--vc-text)] focus-visible:outline-2 focus-visible:outline-offset-2 ' +
          'focus-visible:outline-[var(--vc-focus-ring)]'
        }
      >
        <UserRound aria-hidden="true" className="size-3.5" />
        <span className="max-w-[16ch] truncate">{name}</span>
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Account"
          className={
            'absolute right-0 top-[calc(100%+6px)] z-50 w-64 overflow-hidden rounded-[var(--vc-radius-lg)] ' +
            'border border-[var(--vc-border)] bg-[var(--vc-surface)] shadow-lg'
          }
        >
          <div className="border-b border-[var(--vc-border)] px-3 py-2.5">
            <p className="truncate text-[13px] font-medium text-[var(--vc-text)]">{name}</p>
            <p className="truncate text-[12px] text-[var(--vc-text-subtle)]">{email}</p>

            {platformRoles.length > 0 ? (
              <p className="mt-2 flex items-center gap-1.5 text-[11.5px] text-[var(--vc-text-muted)]">
                <ShieldCheck aria-hidden="true" className="size-3" />
                {platformRoles.join(', ').replaceAll('_', ' ').toLowerCase()}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={auth.signOut}
            className={
              'flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] ' +
              'text-[var(--vc-text)] transition-colors duration-[var(--vc-duration-fast)] ' +
              'hover:bg-[var(--vc-surface-hover)] focus-visible:outline-2 ' +
              'focus-visible:-outline-offset-2 focus-visible:outline-[var(--vc-focus-ring)]'
            }
          >
            <LogOut aria-hidden="true" className="size-3.5" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
